import React, { useState } from "react"
import { useMutation, useQuery } from "urql"

import { Button } from "../../components/shared/Buttons"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import NotificationDeliveryLogTable from "../../components/shared/NotificationDeliveryLogTable"
import GraphQLProvider from "../../providers/GraphQLProvider"

import ClientPageView from "./ClientPageView"

const CLIENT_NOTIFICATION_LOGS_QUERY = `
  query ClientNotificationDeliveryLogsPage($clientId: ID!) {
    client(clientId: $clientId) {
      id
      newsletterSubscriptionStatus
      newsletterPreferencesUrl
      notificationDeliveryLogs(limit: 50) {
        id
        eventType
        deliveryChannel
        body
        bodyRedacted
        subject
        toAddress
        fromAddress
        status
        startedAt
        finishedAt
        enqueuedAt
        createdAt
        durationMs
        errorMessage
        errorCode
      }
    }
  }
`

const SET_NEWSLETTER_SUBSCRIPTION_MUTATION = `
  mutation SetClientNewsletterSubscription($clientId: ID!, $status: String!) {
    setClientNewsletterSubscription(clientId: $clientId, status: $status) {
      result
      errors
      client {
        id
        newsletterSubscriptionStatus
        newsletterPreferencesUrl
      }
    }
  }
`

const NewsletterSubscriptionCard = ({ clientId, initialStatus, preferencesUrl }) => {
  const [status, setStatus] = useState(initialStatus || "subscribed")
  const [, setSubscription] = useMutation(SET_NEWSLETTER_SUBSCRIPTION_MUTATION)
  const [pending, setPending] = useState(false)

  const toggle = async () => {
    const nextStatus = status === "subscribed" ? "unsubscribed" : "subscribed"
    setPending(true)
    const result = await setSubscription({ clientId, status: nextStatus })
    const next = result.data?.setClientNewsletterSubscription?.client?.newsletterSubscriptionStatus
    if (next) setStatus(next)
    setPending(false)
  }

  return (
    <div className="mb-8 rounded-xl border border-gray-light bg-white p-4">
      <h2 className="mb-2 text-lg font-bold">Newsletter subscription</h2>
      <p className="mb-4 text-sm text-gray-dark">
        Control whether this client receives your practice newsletter emails.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold capitalize">{status}</span>
        <Button type="tertiary" disabled={pending} onClick={toggle}>
          {status === "subscribed" ? "Opt out" : "Opt in"}
        </Button>
        {preferencesUrl ? (
          <a
            href={preferencesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-teal hover:underline">
            Client preferences page
          </a>
        ) : null}
      </div>
    </div>
  )
}

const NotificationsContent = ({ client, newslettersEnabled }) => {
  const [{ data, fetching }] = useQuery({
    query: CLIENT_NOTIFICATION_LOGS_QUERY,
    variables: { clientId: client.id },
    requestPolicy: "cache-and-network"
  })

  const notificationLogs = data?.client?.notificationDeliveryLogs

  if (fetching && !data) {
    return <LoadingSpinner />
  }

  return (
    <div className="mb-8">
      {newslettersEnabled ? (
        <NewsletterSubscriptionCard
          clientId={client.id}
          initialStatus={data?.client?.newsletterSubscriptionStatus}
          preferencesUrl={data?.client?.newsletterPreferencesUrl}
        />
      ) : null}
      <h2 className="mb-2 text-lg font-bold">Notification deliveries</h2>
      <p className="mb-4 text-sm text-gray-dark">
        Email, SMS, and push delivery attempts for this client in your practice. Times use your browser&apos;s local
        timezone.
      </p>
      <NotificationDeliveryLogTable logs={notificationLogs || []} />
    </div>
  )
}

const Notifications = ({
  client: clientProp,
  upcomingAppointments,
  clientNotesEnabled,
  multipleIntakeFormsEnabled,
  newslettersEnabled
}) => {
  const [client] = useState(clientProp)

  return (
    <ClientPageView
      client={client}
      upcomingAppointments={upcomingAppointments}
      clientNotesEnabled={clientNotesEnabled}
      multipleIntakeFormsEnabled={multipleIntakeFormsEnabled}
      path={`/portal/clients/${client.id}/notifications`}>
      <NotificationsContent client={client} newslettersEnabled={newslettersEnabled} />
    </ClientPageView>
  )
}

export default function ClientNotificationsPage(props) {
  return (
    <GraphQLProvider>
      <Notifications {...props} />
    </GraphQLProvider>
  )
}
